import EvolutionsCard, {
  OnHandleCompleteProps,
} from "@/components/organisms/Evolutions/EvolutionsCard/EvolutionsCard";
import { EvolutionsScrollContainer } from "@/components/organisms/Evolutions/EvolutionsScroll/EvolutionsScrollElements";
import { fireGTMEvent } from "@/functions/fireGTMEvent";
import { formatTime } from "@/functions/formatTime";
import { evolutionsSelector } from "@/redux/evolutions/evolutionsSlice";
import { BREAKPOINTS } from "@/settings";
import { clsx } from "clsx";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

type Props = {
  activeEvolutions: URLSearchParams;
  setActiveEvolutions: (evolutions: URLSearchParams) => void;
  setShowExpandedEvolutions: React.Dispatch<React.SetStateAction<boolean>>;
  showExpandedEvolutions: boolean;
  excludePaid: boolean;
  isMobile: boolean;
  excludeCompletedEvolutions: boolean;
  onHandleComplete: OnHandleCompleteProps;
};

const EvolutionsSection = ({
  activeEvolutions,
  setActiveEvolutions,
  showExpandedEvolutions,
  excludePaid,
  excludeCompletedEvolutions,
  onHandleComplete: onHandleCompleteProp,
}: Props) => {
  const [expandedCardId, setExpandedCardId] = useState<number | null>(null);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const evolutions = useSelector(evolutionsSelector).evolutions.filter((e) => {
    if (excludePaid && e.cost) return false;
    if (excludeCompletedEvolutions && e.completed) return false;
    return true;
  });

  const [, setSearchParams] = useSearchParams();

  const urlParamFriendlyName = (name: string) =>
    name.toLowerCase().replaceAll(" ", "-").replaceAll(",", "");

  useEffect(() => {
    if (excludePaid) {
      const activeEvosAsStrings = activeEvolutions?.get("active")?.split(",");
      const activeEvosWithoutPaid = evolutions.reduce((filteredEvos, e) => {
        if (
          activeEvosAsStrings?.includes(
            urlParamFriendlyName(e.name.replaceAll(",", "")),
          ) &&
          !e.cost
        ) {
          filteredEvos.push(urlParamFriendlyName(e.name));
        }
        return filteredEvos;
      }, [] as string[]);
      const evoSearchParams = new URLSearchParams(
        activeEvosWithoutPaid.join(","),
      );
      setSearchParams(evoSearchParams);
      setActiveEvolutions(evoSearchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludePaid]);

  const handleActiveEvolutions = (inputName: string) => {
    const evoName = urlParamFriendlyName(inputName);
    const evoSearchParams = new URLSearchParams();
    const evos = activeEvolutions?.get("active")?.split(",");
    if (evos) {
      if (evos.includes(evoName.replaceAll(",", ""))) {
        const newEvos = evos.filter((e) => e !== evoName).join(",");

        if (newEvos !== "") {
          evoSearchParams.append("active", newEvos);
        }
      } else {
        fireGTMEvent("clicked_evolution");
        evoSearchParams.append("active", `${evos},${evoName}`);
      }
    } else {
      fireGTMEvent("clicked_evolution");
      evoSearchParams.append("active", evoName);
    }

    setSearchParams(evoSearchParams);
    setActiveEvolutions(evoSearchParams);
  };

  const onHandleComplete: OnHandleCompleteProps = ({
    id,
    categoryId,
    completed,
  }) => {
    if (completed) {
      setExpandedCardId((prev) => (prev === id ? null : prev));
      const evo = evolutions.find((e) => e.id === id);
      if (evo) {
        const evoName = urlParamFriendlyName(evo.name);
        const current = activeEvolutions.get("active")?.split(",") ?? [];

        // Only update if the evolution is actually in the active list
        if (current.includes(evoName)) {
          const updated = current.filter((name) => name !== evoName);
          const params = new URLSearchParams();

          if (updated.length) params.append("active", updated.join(","));
          setSearchParams(params);
          setActiveEvolutions(params);
        }
      }
    }
    onHandleCompleteProp({ id, categoryId, completed });
  };

  return (
    <EvolutionsScrollContainer expanded={showExpandedEvolutions}>
      <div className={clsx("flex flex-row gap-x-2", isMobile && "-mb-4")}>
        {evolutions.map((e) => {
          const now = new Date().getTime();
          const timeRemaining = formatTime(e.endTime * 1000 - now);
          const isOpen =
            (showExpandedEvolutions && !e.completed) || expandedCardId === e.id;

          return (
            <EvolutionsCard
              key={e.id}
              id={e.id}
              categoryId={e.categoryId}
              title={e.name}
              expanded={isOpen}
              processingState={e.processingState}
              timeRemaining={timeRemaining}
              newEvolution={e.newEvolution}
              isActive={activeEvolutions
                ?.get("active")
                ?.split(",")
                ?.includes(urlParamFriendlyName(e.name.replaceAll(",", "")))}
              handleClick={() => handleActiveEvolutions(e.name)}
              requirements={e.requirements}
              upgrades={e.upgrades}
              cost={e.cost}
              completed={e.completed}
              onActivateEvolution={() => handleActiveEvolutions(e.name)}
              onHandleComplete={onHandleComplete}
            />
          );
        })}
      </div>
    </EvolutionsScrollContainer>
  );
};

export default EvolutionsSection;
